import { useTranslation } from 'react-i18next';
import { differingParts, type ContextProposal } from '../../lib/conversationContext';
import {
  comparisonText,
  contextLineParts,
  formatRange,
  ownWindowText,
  screenLabel,
  shiftsText,
  sourcesText,
  statusesText,
  venuesText,
} from '../../lib/contextSummary';
import type { RequestViewContext } from '../../lib/viewContext';
import { Icon } from '../shared/Icon';
import type { ConversationContextState } from './useMaitreController';

/**
 * A conversation's context in the context bar's words (Phase C): the venues,
 * window, period, comparison and filters its answers count, and how they
 * count. A new chat shows the screen's (its first question records it); a
 * conversation saved before contexts were recorded says so, never a guess.
 */
export function ConversationContextRow({
  state,
  context,
  screenContext,
  lastRestaurantView = false,
}: {
  state: ConversationContextState;
  context: RequestViewContext | null;
  screenContext: RequestViewContext | null;
  /** The screen has no restaurant context: a new chat names the one it uses. */
  lastRestaurantView?: boolean;
}) {
  const { t } = useTranslation('assistant');
  if (state === 'not-recorded') {
    return (
      <div className="cx-line is-conversation is-unrecorded" role="group" aria-label={t('conversation.aria')}>
        <span className="cx-line-screen">{t('conversation.label')}</span>
        <span className="cx-line-part is-strong">
          <Icon name="info" size={12} />
          {t('conversation.notRecorded')}
        </span>
        <span className="cx-line-part is-hint">{t('conversation.notRecordedHint')}</span>
      </div>
    );
  }
  const vc = state === 'recorded' ? context : screenContext;
  if (!vc) return null;
  const { parts, definitions } = contextLineParts(vc);
  const isNew = state === 'new';
  if (isNew && lastRestaurantView) {
    // "New conversation · Your last restaurant view: La Scarpetta Zürich".
    return (
      <div
        className="cx-line is-conversation is-new is-last-view"
        role="group"
        aria-label={t('conversation.aria')}
        data-maitre-context="last-restaurant-view"
      >
        <span className="cx-line-screen">
          {t('conversation.newLabel')}
          <span className="cx-line-screen-of"> · {t('conversation.lastView')}</span>
        </span>
        {parts.map(part => (
          <span
            key={part.key}
            className={`cx-line-part${part.key === 'venues' ? ' is-strong' : ''}${part.muted ? ' is-muted' : ''}`}
          >
            {part.icon ? <Icon name={part.icon} size={12} /> : null}
            {part.text}
          </span>
        ))}
        <span className="cx-line-part is-hint">{t('conversation.lastViewHint')}</span>
      </div>
    );
  }
  return (
    <div className={`cx-line is-conversation${isNew ? ' is-new' : ''}`} role="group" aria-label={t('conversation.aria')}>
      <span className="cx-line-screen">
        {isNew ? t('conversation.newLabel') : t('conversation.label')}
        <span className="cx-line-screen-of"> · {isNew ? t('conversation.newHint') : screenLabel(vc.screen)}</span>
      </span>
      {parts.map(part => (
        <span
          key={part.key}
          className={`cx-line-part${part.key === 'venues' ? ' is-strong' : ''}${part.muted ? ' is-muted' : ''}`}
        >
          {part.icon ? <Icon name={part.icon} size={12} /> : null}
          {part.text}
        </span>
      ))}
      <span className="cx-line-part is-defs">
        <Icon name="info" size={12} />
        {definitions.chip}
      </span>
    </div>
  );
}

/** What the screen shows now that this conversation doesn't, part by part. */
export function screenDifference(conv: RequestViewContext, screen: RequestViewContext): string {
  const parts: string[] = [];
  for (const part of differingParts(conv, screen)) {
    if (part === 'venues') parts.push(venuesText(screen));
    if (part === 'period') parts.push(formatRange(screen.period.start, screen.period.end));
    if (part === 'comparison') parts.push(comparisonText(screen.comparison));
    if (part === 'statuses') parts.push(statusesText(screen.filters.statuses));
    if (part === 'sources') parts.push(sourcesText(screen.filters.sources));
    if (part === 'shifts') parts.push(shiftsText(screen.filters.shifts));
  }
  if (!parts.length) {
    // Only the window differs (Forecast's next N days, Live's today, or none):
    // name what the screen counts over instead.
    const own = ownWindowText(screen.window);
    const period = screen.uses?.period ? formatRange(screen.period.start, screen.period.end) : null;
    parts.push(own ?? period ?? venuesText(screen));
  }
  return parts.join(' · ');
}

/**
 * Asked once, quietly, when the screen's context is not the conversation's:
 * "The screen now shows … · Use the new context / Keep this conversation's".
 */
export function ContextChangePrompt({
  context,
  screenContext,
  onUse,
  onKeep,
}: {
  context: RequestViewContext;
  screenContext: RequestViewContext;
  onUse: () => void;
  onKeep: () => void;
}) {
  const { t } = useTranslation('assistant');
  return (
    <div className="maitre-ctx-prompt" role="status" aria-label={t('conversation.changed.aria')}>
      <p className="maitre-ctx-prompt-text">
        <Icon name="sliders" size={13} />
        <span>
          {t('conversation.changed.lead')} <strong>{screenDifference(context, screenContext)}</strong>
        </span>
      </p>
      <div className="maitre-ctx-actions">
        <button type="button" className="maitre-ctx-btn is-primary" onClick={onUse}>
          {t('conversation.changed.use')}
        </button>
        <button type="button" className="maitre-ctx-btn" onClick={onKeep}>
          {t('conversation.changed.keep')}
        </button>
      </div>
    </div>
  );
}

/** "This needs 1 Mar – 31 May 2026 · Switch this conversation and ask again". */
export function proposalText(proposal: ContextProposal, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const needs: string[] = [];
  if (proposal.period) {
    const range = formatRange(proposal.period.start, proposal.period.end);
    needs.push(
      proposal.comparison
        ? `${range} ${t('conversation.offer.vs', { range: formatRange(proposal.comparison.start, proposal.comparison.end) })}`
        : range,
    );
  }
  if (proposal.venues?.added.length) needs.push(proposal.venues.added.map(v => v.name).join(', '));
  return needs.join(` ${t('conversation.offer.and')} `);
}

export function ContextSwitchOffer({
  proposal,
  onSwitch,
  disabled,
}: {
  proposal: ContextProposal;
  onSwitch: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('assistant');
  const needs = proposalText(proposal, t);
  if (!needs) return null;
  return (
    <div className="maitre-ctx-offer" role="group" aria-label={t('conversation.offer.aria')}>
      <p>
        {t('conversation.offer.lead')} <strong>{needs}</strong>
      </p>
      <button type="button" className="maitre-ctx-btn is-primary" onClick={onSwitch} disabled={disabled}>
        <Icon name="refresh" size={12} />
        {t('conversation.offer.switch')}
      </button>
    </div>
  );
}

/** A change of the conversation's context, where it happened in the thread. */
export function ContextEvent({ kind, context }: { kind: 'recorded' | 'changed'; context: RequestViewContext }) {
  const { t } = useTranslation('assistant');
  const { parts } = contextLineParts(context);
  return (
    <div className="maitre-ctx-event" role="note">
      <Icon name="sliders" size={12} />
      <span>
        {kind === 'changed' ? t('conversation.event.changed') : t('conversation.event.recorded')}{' '}
        <strong>{parts.map(p => p.text).join(' · ')}</strong>
      </span>
    </div>
  );
}
